import { create } from 'zustand';
import { CameraControls } from '@react-three/drei';
import { BlockConfig } from './MotionSequencer';
import { MotionBlockOptions } from './motionBlocks';
import * as THREE from 'three';
import { getAllFiles, saveFile, getFile, deleteFile, migrateFromLocalStorage, type ExportedFile } from './fileStorage';

export interface SavedSequence {
  id: string;
  name: string;
  blocks: BlockConfig[];
  createdAt: number;
  updatedAt: number;
}

// ExportedFile is now imported from fileStorage.ts

interface VideoRecorderControls {
  isRecording: boolean;
  isProcessing: boolean;
  progress: number;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  downloadVideo: (filename?: string) => Promise<void>;
}

interface MotionState {
  blocks: BlockConfig[];
  activeBlockId: string | null; // Currently editing Block ID
  controlsRef: CameraControls | null; // Store CameraControls instance
  showBezierDebug: boolean; // Toggle for bezier curve debug visualization
  videoRecorder: VideoRecorderControls | null; // Video recorder controls
  modelUrl: string; // Current model URL (can be file path or object URL)
  modelFileExtension: string; // File extension to determine which renderer to use

  setControls: (controls: CameraControls) => void;
  setVideoRecorder: (recorder: VideoRecorderControls) => void;
  setModelUrl: (url: string, extension?: string) => void;
  addBlock: (type: string) => void;
  updateBlock: (id: string, data: Partial<MotionBlockOptions>) => void;
  removeBlock: (id: string) => void;
  setActiveBlock: (id: string | null) => void;
  setBlocks: (blocks: BlockConfig[]) => void;
  moveBlockUp: (id: string) => void;
  moveBlockDown: (id: string) => void;
  toggleBezierDebug: () => void;
  toggleBlockEnabled: (id: string) => void;
  
  // Core feature: Capture current camera state to current Block
  captureCameraToBlock: (field: 'startState' | 'endState' | 'targetPosition' | 'cameraPosition') => void;
  
  // Save/Load sequences
  saveSequence: (name: string) => string; // Returns sequence ID
  loadSequence: (id: string) => void;
  deleteSequence: (id: string) => void;
  getSavedSequences: () => SavedSequence[];
  clearBlocks: () => void;
  
  // Export/Import to/from file system (managed in IndexedDB)
  exportSequenceToFiles: (name: string) => Promise<string>; // Returns file ID
  loadSequenceFromFiles: (id: string) => Promise<void>;
  deleteExportedFile: (id: string) => Promise<void>;
  getExportedFiles: () => Promise<ExportedFile[]>;
  downloadExportedFile: (id: string) => Promise<void>; // Download as JSON file
  importFileToStorage: (fileContent: string, name?: string) => Promise<boolean>; // Import and add to storage
}

// Re-export ExportedFile type
export type { ExportedFile } from './fileStorage';

// Helper function: Get default options for different block types
// Note: id is NOT included here - it's set in addBlock to ensure uniqueness
const getDefaultOptions = (type: string): Partial<BlockConfig> => {
  const base = { duration: 2, ease: 'power2.inOut' };
  
  if (type === 'moveTo') {
    return { ...base };
  }
  if (type === 'composite') {
    return { ...base, duration: 3 };
  }
  if (type === 'dolly') {
    return { ...base, distanceDelta: 1 };
  }
  if (type === 'arc') {
    return { ...base, duration: 3, arcAngle: Math.PI / 2 };
  }
  if (type === 'pan') {
    return { ...base, angleDelta: Math.PI / 4 }; // Default: 45 degrees
  }
  if (type === 'tilt') {
    return { ...base, angleDelta: Math.PI / 6 }; // Default: 30 degrees
  }
  if (type === 'pedestal') {
    return { ...base, truckY: 1 };
  }
  if (type === 'roll') {
    return { ...base, angleDelta: Math.PI / 6 }; // Default: 30 degrees
  }
  if (type === 'zoom') {
    return { ...base, zoomFov: 20 }; // Default: zoom in to 20
  }
  if (type === 'dollyZoom') {
    return { ...base, duration: 3, zoomFov: 10 }; // Default: 3s, zoom to 10
  }
  if (type === 'truck') {
    return { ...base, truckAmount: 0.5 };
  }
  if (type === 'bezierCurve') {
    return { 
      ...base, 
      duration: 3,
      bezierCurve: {
        controlPoints: [], // Empty array, will use current camera position as default
        lookAtTarget: [0, 0, 0], // Default to origin
        maintainOrientation: false
      }
    };
  }
  
  return { ...base };
};

export const useMotionStore = create<MotionState>((set, get) => ({
  blocks: [],
  activeBlockId: null,
  controlsRef: null,
  showBezierDebug: true, // Default to showing debug
  videoRecorder: null,
  modelUrl: "/models/splats/hoodie_msbhv.splat", // Default model
  modelFileExtension: ".splat", // Default extension

  setControls: (controls) => set({ controlsRef: controls }),
  setVideoRecorder: (recorder) => set({ videoRecorder: recorder }),
  setModelUrl: (url, extension) => {
    // Extract extension from URL if not provided
    let fileExtension = extension;
    if (!fileExtension) {
      // Try to extract from URL
      const urlLower = url.toLowerCase();
      if (urlLower.endsWith('.ply')) {
        fileExtension = '.ply';
      } else if (urlLower.endsWith('.splat')) {
        fileExtension = '.splat';
      } else {
        // Default to .splat if can't determine
        fileExtension = '.splat';
      }
    }
    set({ modelUrl: url, modelFileExtension: fileExtension });
  },
  toggleBezierDebug: () => set((state) => ({ showBezierDebug: !state.showBezierDebug })),
  toggleBlockEnabled: (id) => set((state) => ({
    blocks: state.blocks.map((b) => 
      b.id === id ? { ...b, enabled: b.enabled === false ? true : false } : b
    )
  })),

  addBlock: (type) => {
    const timestamp = Date.now();
    const newBlock: BlockConfig = {
      id: `${type}-${timestamp}`,
      ...getDefaultOptions(type)
    } as BlockConfig;
    
    set((state) => ({
      blocks: [...state.blocks, newBlock],
      activeBlockId: newBlock.id
    }));
  },

  updateBlock: (id, data) => set((state) => ({
    blocks: state.blocks.map((b) => 
      b.id === id ? { ...b, ...data } : b
    )
  })),

  removeBlock: (id) => set((state) => {
    const newBlocks = state.blocks.filter((b) => b.id !== id);
    return {
      blocks: newBlocks,
      activeBlockId: state.activeBlockId === id 
        ? (newBlocks.length > 0 ? newBlocks[0].id : null)
        : state.activeBlockId
    };
  }),

  setActiveBlock: (id) => set({ activeBlockId: id }),

  setBlocks: (blocks) => set({ blocks }),

  moveBlockUp: (id) => set((state) => {
    const index = state.blocks.findIndex(b => b.id === id);
    if (index <= 0) return state; // Already at top or not found
    
    const newBlocks = [...state.blocks];
    [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
    return { blocks: newBlocks };
  }),

  moveBlockDown: (id) => set((state) => {
    const index = state.blocks.findIndex(b => b.id === id);
    if (index < 0 || index >= state.blocks.length - 1) return state; // Already at bottom or not found
    
    const newBlocks = [...state.blocks];
    [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
    return { blocks: newBlocks };
  }),

  captureCameraToBlock: (field) => {
    const { controlsRef, activeBlockId, updateBlock } = get();
    if (!controlsRef || !activeBlockId) {
      console.warn('Cannot capture: controls or activeBlockId not set');
      return;
    }

    // Get current camera data
    const pos = new THREE.Vector3();
    const target = new THREE.Vector3();
    controlsRef.getPosition(pos);
    controlsRef.getTarget(target);

    // Calculate roll angle from camera's up vector
    // Roll is the rotation around the camera's forward axis
    const forward = new THREE.Vector3();
    controlsRef.camera.getWorldDirection(forward);
    const currentUp = controlsRef.camera.up.clone();
    
    // Use world up (0, 1, 0) as reference, but project it perpendicular to forward
    const worldUp = new THREE.Vector3(0, 1, 0);
    let referenceUp = worldUp.clone();
    
    // Project worldUp onto plane perpendicular to forward
    const forwardDot = referenceUp.dot(forward);
    referenceUp.sub(forward.clone().multiplyScalar(forwardDot));
    
    // If referenceUp is too small (camera looking straight up/down), use a different reference
    if (referenceUp.length() < 0.001) {
      // Use camera's right vector as reference instead
      const right = new THREE.Vector3();
      right.crossVectors(currentUp, forward).normalize();
      if (right.length() > 0.001) {
        referenceUp.crossVectors(forward, right).normalize();
      } else {
        // Fallback: use a fixed reference
        referenceUp.set(1, 0, 0);
        referenceUp.sub(forward.clone().multiplyScalar(referenceUp.dot(forward))).normalize();
      }
    } else {
      referenceUp.normalize();
    }
    
    // Calculate angle between currentUp and referenceUp around forward axis
    const dot = Math.max(-1, Math.min(1, currentUp.dot(referenceUp)));
    const angle = Math.acos(dot);
    
    // Determine sign using cross product
    const cross = new THREE.Vector3();
    cross.crossVectors(referenceUp, currentUp);
    const sign = cross.dot(forward) >= 0 ? 1 : -1;
    const roll = angle * sign;

    // Build camera state object
    const cameraState: any = {
      azimuth: controlsRef.azimuthAngle,
      polar: controlsRef.polarAngle,
      distance: controlsRef.distance,
      center: [target.x, target.y, target.z] as [number, number, number],
      roll: roll
    };

    // Add FOV if camera is PerspectiveCamera
    if (controlsRef.camera instanceof THREE.PerspectiveCamera) {
      cameraState.fov = controlsRef.camera.fov;
    }

    // Fill data based on field to set
    if (field === 'cameraPosition') {
      updateBlock(activeBlockId, { 
        cameraPosition: [pos.x, pos.y, pos.z] 
      });
    } else if (field === 'targetPosition') {
      updateBlock(activeBlockId, { 
        targetPosition: [target.x, target.y, target.z] 
      });
    } else if (field === 'startState') {
      updateBlock(activeBlockId, {
        startState: cameraState
      });
    } else if (field === 'endState') {
      updateBlock(activeBlockId, {
        endState: cameraState
      });
    }
  },

  clearBlocks: () => set({ blocks: [], activeBlockId: null }),

  saveSequence: (name: string) => {
    const { blocks } = get();
    const now = Date.now();
    const id = `seq-${now}`;
    
    const sequence: SavedSequence = {
      id,
      name,
      blocks: JSON.parse(JSON.stringify(blocks)), // Deep clone
      createdAt: now,
      updatedAt: now
    };

    // Get existing sequences from localStorage
    const existing = localStorage.getItem('motionSequences');
    const sequences: SavedSequence[] = existing ? JSON.parse(existing) : [];
    
    // Add new sequence
    sequences.push(sequence);
    
    // Save back to localStorage
    localStorage.setItem('motionSequences', JSON.stringify(sequences));
    
    return id;
  },

  loadSequence: (id: string) => {
    const existing = localStorage.getItem('motionSequences');
    if (!existing) return;
    
    const sequences: SavedSequence[] = JSON.parse(existing);
    const sequence = sequences.find(s => s.id === id);
    
    if (sequence) {
      set({ 
        blocks: sequence.blocks,
        activeBlockId: sequence.blocks.length > 0 ? sequence.blocks[0].id : null
      });
    }
  },

  deleteSequence: (id: string) => {
    const existing = localStorage.getItem('motionSequences');
    if (!existing) return;
    
    const sequences: SavedSequence[] = JSON.parse(existing);
    const filtered = sequences.filter(s => s.id !== id);
    localStorage.setItem('motionSequences', JSON.stringify(filtered));
  },

  getSavedSequences: (): SavedSequence[] => {
    const existing = localStorage.getItem('motionSequences');
    if (!existing) return [];
    
    const sequences: SavedSequence[] = JSON.parse(existing);
    // Sort by updatedAt descending (most recent first)
    return sequences.sort((a, b) => b.updatedAt - a.updatedAt);
  },

  // Export sequence to files storage (IndexedDB)
  exportSequenceToFiles: async (name: string) => {
    const { blocks } = get();
    const now = Date.now();
    const id = `file-${now}`;
    
    const data = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      blocks: blocks
    };
    
    const fileContent = JSON.stringify(data, null, 2);
    
    const exportedFile: ExportedFile = {
      id,
      name,
      blocks: JSON.parse(JSON.stringify(blocks)), // Deep clone
      exportedAt: now,
      fileContent
    };

    try {
      await saveFile(exportedFile);
      return id;
    } catch (error) {
      console.error('Failed to save file:', error);
      alert('Failed to save file');
      return '';
    }
  },

  // Load sequence from files storage
  loadSequenceFromFiles: async (id: string) => {
    try {
      const file = await getFile(id);
      if (file) {
        set({ 
          blocks: file.blocks,
          activeBlockId: file.blocks.length > 0 ? file.blocks[0].id : null
        });
      }
    } catch (error) {
      console.error('Failed to load file:', error);
      alert('Failed to load file');
    }
  },

  // Delete exported file
  deleteExportedFile: async (id: string) => {
    try {
      await deleteFile(id);
    } catch (error) {
      console.error('Failed to delete file:', error);
      alert('Failed to delete file');
    }
  },

  // Get all exported files
  getExportedFiles: async (): Promise<ExportedFile[]> => {
    try {
      // Migrate from localStorage on first run
      await migrateFromLocalStorage();
      return await getAllFiles();
    } catch (error) {
      console.error('Failed to get files:', error);
      return [];
    }
  },

  // Download exported file as JSON file
  downloadExportedFile: async (id: string) => {
    try {
      const file = await getFile(id);
      if (!file) return;
      
      const blob = new Blob([file.fileContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${file.name}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('Failed to download file');
    }
  },

  // Import file and add to storage
  importFileToStorage: async (fileContent: string, name?: string): Promise<boolean> => {
    try {
      const data = JSON.parse(fileContent);
      
      // Support both old format (just blocks array) and new format (with metadata)
      const blocksToImport = data.blocks || data;
      
      if (!Array.isArray(blocksToImport)) {
        throw new Error('Invalid file format: blocks must be an array');
      }
      
      // Validate blocks structure
      for (const block of blocksToImport) {
        if (!block.id || typeof block.id !== 'string') {
          throw new Error('Invalid block format: missing or invalid id');
        }
      }
      
      const now = Date.now();
      const id = `file-${now}`;
      const fileName = name || `imported-${new Date().toLocaleDateString()}`;
      
      const exportedFile: ExportedFile = {
        id,
        name: fileName,
        blocks: blocksToImport,
        exportedAt: now,
        fileContent
      };

      await saveFile(exportedFile);
      return true;
    } catch (error) {
      console.error('Failed to import file:', error);
      alert(`Failed to import file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return false;
    }
  }
}));

